import { useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { ThemeProvider, CssBaseline } from '@mui/material';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { trpc, trpcClient, queryClient } from './lib/trpc';
import { lightTheme, darkTheme } from './theme';
import { AuthProvider } from './lib/AuthContext';

// Google OAuth Client ID from environment
const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID || '';

// Layout components
import { AppLayout, AuthLayout } from './components/Layout';
import ProtectedRoute from './components/ProtectedRoute';
import AdminRoute from './components/AdminRoute';

// Auth pages
import { LoginPage, RegisterPage, ForgotPasswordPage, ResetPasswordPage, VerifyEmailPage } from './pages/auth';

// Protected pages
import {
  MonitorsPage,
  MonitorDetailPage,
  AddMonitorPage,
  EditMonitorPage,
} from './pages/monitors';
import {
  StatusPagesListPage,
  StatusPageDetailPage,
  CreateStatusPage,
  EditStatusPage,
} from './pages/status-pages';
import { NotificationChannelsPage, AddChannelPage, EditChannelPage } from './pages/notifications';
import { SettingsPage } from './pages/settings';
import { BillingPage } from './pages/billing/BillingPage';

// Public pages (no authentication required)
import { PublicStatusPage } from './pages/public';
import { RootRedirect } from './pages/RootRedirect';

// Admin pages
import { AdminDashboardPage } from './pages/admin';

/**
 * Inner app component that uses AuthProvider
 * Must be inside BrowserRouter for useNavigate to work
 */
function AppRoutes() {
  return (
    <AuthProvider>
      <Routes>
        {/* Root redirect - authenticated users go to /monitors, others to marketing site */}
        <Route path="/" element={<RootRedirect />} />

        {/* Public status page route - NO authentication required */}
        <Route path="/status/:slug" element={<PublicStatusPage />} />

        {/* Auth routes - public */}
        <Route element={<AuthLayout />}>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
          <Route path="/verify-email/:token" element={<VerifyEmailPage />} />
        </Route>

        {/* Protected routes - require authentication */}
        <Route element={<ProtectedRoute />}>
          <Route element={<AppLayout />}>
            {/* Monitors - Landing page for authenticated users */}
            <Route path="/monitors" element={<MonitorsPage />} />
            <Route path="/monitors/new" element={<AddMonitorPage />} />
            <Route path="/monitors/:id" element={<MonitorDetailPage />} />
            <Route path="/monitors/:id/edit" element={<EditMonitorPage />} />

            {/* Status Pages (protected management routes) */}
            <Route path="/status-pages" element={<StatusPagesListPage />} />
            <Route path="/status-pages/new" element={<CreateStatusPage />} />
            <Route path="/status-pages/:id" element={<StatusPageDetailPage />} />
            <Route path="/status-pages/:id/edit" element={<EditStatusPage />} />

            {/* Notifications */}
            <Route path="/notifications" element={<NotificationChannelsPage />} />
            <Route path="/notifications/new" element={<AddChannelPage />} />
            <Route path="/notifications/:id/edit" element={<EditChannelPage />} />

            {/* Settings */}
            <Route path="/settings" element={<SettingsPage />} />

            {/* Billing */}
            <Route path="/billing" element={<BillingPage />} />
          </Route>

          {/* Admin routes - require admin access */}
          <Route element={<AdminRoute />}>
            <Route element={<AppLayout />}>
              <Route path="/admin" element={<AdminDashboardPage />} />
            </Route>
          </Route>
        </Route>

        {/* Catch-all redirect to landing page */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </AuthProvider>
  );
}

/**
 * Main App component
 * Sets up providers and routing
 */
function App() {
  // Theme state (light mode by default)
  const [isDarkMode] = useState(false);
  const theme = isDarkMode ? darkTheme : lightTheme;

  return (
    <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
      <trpc.Provider client={trpcClient} queryClient={queryClient}>
        <QueryClientProvider client={queryClient}>
          <ThemeProvider theme={theme}>
            <CssBaseline />
            <BrowserRouter basename="/app">
              <AppRoutes />
            </BrowserRouter>
          </ThemeProvider>
        </QueryClientProvider>
      </trpc.Provider>
    </GoogleOAuthProvider>
  );
}

export default App;
